// src/services/EmailNotificationService.js
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase';

const functions = getFunctions();

/**
 * Send an email notification to a user about account updates
 * @param {Object} params - Email parameters
 * @param {string} params.userEmail - Recipient email
 * @param {string} params.userName - Recipient name
 * @param {Object} params.changes - Object describing what changed
 * @param {string} params.message - Custom message from admin
 * @param {string} params.adminName - Name of admin making changes
 * @returns {Promise<Object>} - Result of email send
 */
export const sendUserUpdateNotification = async ({
  userEmail,
  userName,
  changes,
  message,
  adminName
}) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // Call the cloud function
    const sendNotification = httpsCallable(functions, 'sendUserUpdateNotification');
    const result = await sendNotification({
      userEmail,
      userName,
      changes,
      message,
      adminName: adminName || currentUser.displayName || 'Administrator'
    });
    
    return result.data;
  } catch (error) {
    console.error('Error sending user update notification:', error);
    throw error;
  }
};

/**
 * Send a test email (for development/testing)
 * In production, this should be restricted to admins only
 */
export const sendTestEmail = async (recipientEmail) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }
    
    return await sendUserUpdateNotification({
      userEmail: recipientEmail,
      userName: 'Test User',
      changes: {
        role: { old: 'user', new: 'service_provider' },
        platformFeePercentage: { old: 20, new: 15 }
      },
      message: 'This is a test email to verify the email notification system is working correctly.',
      adminName: currentUser.displayName
    });
  } catch (error) {
    console.error('Error sending test email:', error);
    throw error;
  }
};

/**
 * Send an email notification to a new user to approve a listing and create an account
 * @param {Object} params - Email parameters
 * @param {string} params.recipientEmail - Recipient email
 * @param {string} params.recipientName - Recipient name
 * @param {string} params.listingTitle - Title of the listing
 * @param {string} params.approvalLink - Link to approve the listing
 * @param {string} params.adminName - Name of admin creating the listing
 * @returns {Promise<Object>} - Result of email send
 */
export const sendListingApprovalEmail = async ({
  recipientEmail,
  recipientName,
  listingTitle,
  approvalLink,
  adminName
}) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // Call the cloud function
    const sendApproval = httpsCallable(functions, 'sendListingApprovalEmail');
    const result = await sendApproval({
      recipientEmail,
      recipientName,
      listingTitle,
      approvalLink,
      adminName: adminName || currentUser.displayName || 'Administrator'
    });
    
    return result.data;
  } catch (error) {
    console.error('Error sending listing approval email:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error object:', error);
    
    // Fallback to mock implementation for development
    if (error.code === 'functions/not-found' || error.code === 'functions/internal' || error.message.includes('CORS')) {
      console.log('🔄 Falling back to mock email implementation due to:', error.code || error.message);
      return mockSendListingApprovalEmail({
        recipientEmail,
        recipientName,
        listingTitle,
        approvalLink,
        adminName
      });
    }
    throw error;
  }
};

// Note: For local development without Cloud Functions deployed,
// you can use a mock implementation
export const mockSendUserUpdateNotification = async ({
  userEmail,
  userName,
  changes,
  message,
  adminName
}) => {
  console.log('Mock Email Notification:', {
    to: userEmail,
    subject: 'Your RefoundEd Account Has Been Updated',
    userName,
    changes,
    message,
    adminName
  });
  
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    success: true,
    message: 'Email sent successfully (mock)'
  };
};

export const mockSendListingApprovalEmail = async ({
  recipientEmail,
  recipientName,
  listingTitle,
  approvalLink,
  adminName
}) => {
  console.log('Mock Listing Approval Email:', {
    to: recipientEmail,
    subject: 'Approve Your RefoundEd Listing',
    recipientName,
    listingTitle,
    approvalLink,
    adminName
  });
  
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    success: true,
    message: 'Approval email sent successfully (mock)'
  };
};